import React, { useState, useEffect, lazy } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import DetailTab from './DetailTab';
import TaskTab from './TaskTab';
// ✅ HIDDEN: Temporarily hidden tabs
// import ProposalTab from './ProposalTab';
// import ContractTab from './ContractTab';
import api from '../../../../api';
import Alert from '../../../../components/Alert/Alert';

const LeadDetail = ({ lead: propLead }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Use prop lead if provided (for backward compatibility), otherwise fetch from API
  const [lead, setLead] = useState(propLead || null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskCount, setTaskCount] = useState(0);
  const [proposalCount, setProposalCount] = useState(0);
  const [contractCount, setContractCount] = useState(0);
  const [proposalData, setProposalData] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });

  // Loading states for individual tabs
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);

  // Active tab state
  const [activeTab, setActiveTab] = useState('details');

  // Fetch lead if coming from route (no prop provided)
  useEffect(() => {
    const fetchLead = async () => {
      if (!propLead && id) {
        try {
          setLoading(true);
          const response = await api.get(`/crm/leads/${id}`);
          setLead(response.data);
          setLeadDetail(response.data);

          // Fetch proposal data if lead exists
          try {
            const proposalResponse = await api.get(`/crm/proposals/lead/${response.data.id}`);
            if (proposalResponse.data && proposalResponse.data.length > 0) {
              // Get the first proposal (assuming one proposal per lead)
              setProposalData(proposalResponse.data[0]);
            } else {
              setProposalData(null);
            }
          } catch (proposalError) {
            // Proposal not found or not converted yet
            setProposalData(null);
          }
        } catch (error) {
          console.error('Failed to fetch lead:', error);
          setAlert({
            show: true,
            type: 'danger',
            message: 'Failed to load lead details. Please try again.'
          });
          setLead(null);
          setLeadDetail(null);
          setProposalData(null);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchLead();
  }, [id, propLead]);

  // Fetch lead detail if lead prop is provided
  useEffect(() => {
    if (!propLead || !lead || !lead.id) {
      if (propLead) {
        setLeadDetail(null);
        setProposalData(null);
        setLoading(false);
      }
      return;
    }

    const fetchLeadDetail = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/crm/leads/${lead.id}`);
        setLeadDetail(response.data);

        // Fetch proposal data if lead exists
        try {
          const proposalResponse = await api.get(`/crm/proposals/lead/${lead.id}`);
          if (proposalResponse.data && proposalResponse.data.length > 0) {
            // Get the first proposal (assuming one proposal per lead)
            setProposalData(proposalResponse.data[0]);
          } else {
            setProposalData(null);
          }
        } catch (proposalError) {
          // Proposal not found or not converted yet
          setProposalData(null);
        }
      } catch (error) {
        console.error('Failed to fetch lead', error);
        setLeadDetail(null);
        setProposalData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetail();
  }, [lead, propLead]);

  const refreshLeadDetail = async () => {
    const leadId = lead?.id || id;
    if (!leadId) return;
    try {
      const response = await api.get(`/crm/leads/${leadId}`);
      setLeadDetail(response.data);
    } catch (error) {
      console.error('Failed to refresh lead detail:', error);
    }
  };

  // Handle tab change with loading states
  const handleTabChange = (tabName) => {
    // Set loading immediately for better UX
    if (tabName === 'details') {
      setDetailsLoading(true);
    } else if (tabName === 'tasks') {
      setTasksLoading(true);
    } else if (tabName === 'proposals') {
      setProposalsLoading(true);
    } else if (tabName === 'contracts') {
      setContractsLoading(true);
    }

    setActiveTab(tabName);

    // Hide loading after a short delay to simulate content loading
    setTimeout(() => {
      if (tabName === 'details') {
        setDetailsLoading(false);
      } else if (tabName === 'tasks') {
        setTasksLoading(false);
      } else if (tabName === 'proposals') {
        setProposalsLoading(false);
      } else if (tabName === 'contracts') {
        setContractsLoading(false);
      }
    }, 300); // Reduced from 500ms for better responsiveness
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/leads');
    }
  };

  const handleNavigateToProposal = (proposal) => {
    // Use lead ID for navigation since that's how the routing is set up
    navigate(`/proposals/detail/${id}`);
  };

  const hideAlert = () => {
    setAlert({ show: false, type: 'success', message: '' });
  };

  // Show loading spinner for initial page load
  if (loading) {
    return (
      <div className="container-xl">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading lead details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error if lead not found (only when using route, not prop)
  if (!propLead && !lead) {
    return (
      <div className="container-xl">
        {alert.show && (
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={hideAlert}
          />
        )}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-white d-none d-sm-inline-block me-3"
                  onClick={handleBack}
                >
                  <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                  Back
                </button>
                <h3 className="page-title mb-0">Lead Details</h3>
              </div>
            </div>
          </div>
        </div>
        <div className="page-body">
          <div className="alert alert-warning" role="alert">
            Lead not found or you don't have permission to view it.
          </div>
        </div>
      </div>
    );
  }

  // Show page header when accessed via route
  const showPageHeader = !propLead && id;

  return (
    <div className="container-xl">
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={hideAlert}
        />
      )}

      {showPageHeader && (
        <>
          <div className="page-header d-print-none">
            <div className="row align-items-center">
              <div className="col">
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-outline-white d-none d-sm-inline-block me-3"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                    Back
                  </button>
                  <h3 className="page-title mb-0">Lead Details</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-md-8 mt-3">
              <p className="mb-2">
                <span className="text-muted fw-normal">Company Name: </span>
                {lead?.companyId ? (
                  <a
                    href={`/clients/${lead.companyId}`}
                    className="text-primary text-decoration-none fw-bold"
                    onClick={(e) => {
                      // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                      if (e.ctrlKey || e.metaKey || e.button === 1) {
                        return; // Let browser handle it
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/clients/${lead.companyId}`);
                    }}
                    onAuxClick={(e) => {
                      // Handle middle mouse button click
                      if (e.button === 1) {
                        return; // Let browser handle it
                      }
                    }}
                    title={`View client details for ${lead.companyName}`}
                    aria-label={`View client details for ${lead.companyName}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/clients/${lead.companyId}`);
                      }
                    }}
                  >
                    {lead.companyName}
                  </a>
                ) : (
                  <span className="text-dark fw-bold">{lead?.companyName}</span>
                )}
              </p>
              <p className="mb-2">
                <span className="text-muted fw-normal">Project Name: </span>
                <span className="text-dark fw-bold">{lead?.projectName}</span>
              </p>
              {proposalData && (
                <p className="mb-2">
                  <span className="text-muted fw-normal">Proposal Number: </span>
                  <a
                    href={`/proposals/detail/${id}`}
                    className="text-primary text-decoration-none fw-bold"
                    onClick={(e) => {
                      // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                      if (e.ctrlKey || e.metaKey || e.button === 1) {
                        return; // Let browser handle it
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigateToProposal(proposalData);
                    }}
                    onAuxClick={(e) => {
                      // Handle middle mouse button click
                      if (e.button === 1) {
                        return; // Let browser handle it
                      }
                    }}
                    title={`View proposal details for ${proposalData.proposalCode}`}
                    aria-label={`View proposal details for ${proposalData.proposalCode}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNavigateToProposal(proposalData);
                      }
                    }}
                  >
                    {proposalData.proposalCode}
                  </a>
                </p>
              )}
            </div>
          </div>

        </>
      )}

      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
            <li className="nav-item-tab" role="presentation">
              <a
                href="#tabs-details"
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                data-bs-toggle="tab"
                aria-selected={activeTab === 'details'}
                role="tab"
                onClick={() => handleTabChange('details')}
              >
                Details
              </a>
            </li>
            <li className="nav-item-tab" role="presentation">
              <a
                href="#tabs-tasks"
                className={`nav-link ${activeTab === 'tasks' ? 'active' : ''}`}
                data-bs-toggle="tab"
                aria-selected={activeTab === 'tasks'}
                role="tab"
                tabIndex="-1"
                onClick={() => handleTabChange('tasks')}
              >
                Tasks
                {/* Always show count, even during loading */}
                {taskCount > 0 && (
                  <span className="badge bg-blue text-blue-fg badge-pill ms-2">{taskCount}</span>
                )}
              </a>
            </li>
            {/* ✅ HIDDEN: Proposals Tab - Temporarily hidden */}
            {/* <li className="nav-item-tab" role="presentation">
              <a 
                href="#tabs-proposals" 
                className={`nav-link ${activeTab === 'proposals' ? 'active' : ''}`} 
                data-bs-toggle="tab" 
                aria-selected={activeTab === 'proposals'} 
                role="tab" 
                tabIndex="-1"
                onClick={() => handleTabChange('proposals')}
              >
                Proposals
                {proposalCount > 0 && (
                  <span className="badge bg-blue text-blue-fg badge-pill ms-2">{proposalCount}</span>
                )}
              </a>
            </li> */}
            {/* ✅ HIDDEN: Contracts Tab - Temporarily hidden */}
            {/* <li className="nav-item-tab" role="presentation">
              <a 
                href="#tabs-contracts" 
                className={`nav-link ${activeTab === 'contracts' ? 'active' : ''}`} 
                data-bs-toggle="tab" 
                aria-selected={activeTab === 'contracts'} 
                role="tab" 
                tabIndex="-1"
                onClick={() => handleTabChange('contracts')}
              >
                Contracts
                {contractCount > 0 && (
                  <span className="badge bg-blue text-blue-fg badge-pill ms-2">{contractCount}</span>
                )}
              </a>
            </li> */}
          </ul>
        </div>
        <div className="card-body">
          <div className="tab-content">
            <div className={`tab-pane ${activeTab === 'details' ? 'active show' : ''}`} id="tabs-details" role="tabpanel">
              {detailsLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <DetailTab lead={lead} leadDetail={leadDetail} loading={loading} onRefreshLeadDetail={refreshLeadDetail} />
              )}
            </div>
            <div className={`tab-pane ${activeTab === 'tasks' ? 'active show' : ''}`} id="tabs-tasks" role="tabpanel">
              <TaskTab lead={lead} onCountChange={setTaskCount} isLoading={tasksLoading} onTaskChange={refreshLeadDetail} />
            </div>
            {/* ✅ HIDDEN: Proposals Tab Content - Temporarily hidden */}
            {/* <div className={`tab-pane ${activeTab === 'proposals' ? 'active show' : ''}`} id="tabs-proposals" role="tabpanel">
              <ProposalTab lead={lead} leadId={lead?.id} isLoading={proposalsLoading} onCountChange={setProposalCount} />
            </div> */}
            {/* ✅ HIDDEN: Contracts Tab Content - Temporarily hidden */}
            {/* <div className={`tab-pane ${activeTab === 'contracts' ? 'active show' : ''}`} id="tabs-contracts" role="tabpanel">
              <ContractTab leadId={lead?.id} isLoading={contractsLoading} onCountChange={setContractCount} />
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail; 